/**
 * Le jeton « [image] » du cahier de textes, et ce qu'on en fait à l'écran.
 *
 * **Ici et non dans `parent/`, parce que l'enfant le lit aussi.** Le 8 septembre
 * 2026 le travail demandé par les professeurs est arrivé en tête de « Mon
 * travail » (`child/DevoirsDuCahier`), et la coquille enfant n'a pas le droit
 * d'importer `parent/` — la règle est écrite au pied de `parent/effort` et un
 * test balaie `src/child/**`. C'est le second déplacement de cette forme après
 * le sélecteur de période : une recopie aurait fini par compter les images
 * autrement d'un côté que de l'autre.
 */

/**
 * Un professeur qui photographie son tableau écrit « [image] » chez nous.
 *
 * Le connecteur remplace chaque balise ``<img>`` par ce jeton plutôt que de la
 * supprimer, et il a raison : une séance qui n'est qu'une photo ne doit pas se
 * lire comme une séance vide. Mais rendu tel quel à un parent, « [image]
 * [image] [image] » ressemble à une panne d'affichage — c'est exactement ce
 * qu'un parent a lu le 6 septembre 2026 sur le cours d'histoire-géographie.
 *
 * D'où une phrase quand il n'y a *que* des images : le nombre est conservé, et
 * la seule chose vraie est dite, à savoir que le contenu existe et qu'il n'est
 * lisible qu'à sa source. Un texte accompagné d'images garde ses jetons en
 * place : là, ils marquent une position dans la phrase du professeur.
 */
export function imagesSeules(contenu: string): number {
  const jetons = contenu.match(/\[image\]/g);
  if (!jetons) return 0;
  return contenu.replace(/\[image\]/g, " ").trim() === "" ? jetons.length : 0;
}

/**
 * Ce qu'on lit à la place du contenu quand le professeur n'a collé qu'une image.
 *
 * Le connecteur écrit « [image] » là où il y avait une balise — rendu tel quel,
 * ça ressemble à une panne d'affichage, et c'est ce qu'un parent a lu le
 * 6 septembre 2026. Les images du professeur ne traversent pas toutes les
 * routes : là où elles manquent, la phrase dit ce qui est vrai — le contenu
 * existe, il est lisible sur EcoleDirecte — plutôt que de laisser un cadre vide.
 *
 * **Ici depuis le 15 septembre 2026**, l'écran « Travail demandé » du parent
 * ayant besoin de la même phrase que la carte de l'enfant. Elle vivait dans
 * `child/DevoirsDuCahier`, que la coquille parent aurait pu importer — mais
 * l'inverse est interdit et un test le balaie, si bien qu'une phrase partagée
 * n'a qu'un seul endroit où vivre. Troisième déplacement de cette forme, après
 * le sélecteur de période et `imagesSeules`.
 */
export function contenuLisible(contenu: string): string {
  const seules = imagesSeules(contenu);
  if (seules === 0) return contenu;
  return seules === 1
    ? "Le professeur a mis une image ; elle est sur EcoleDirecte."
    : `Le professeur a mis ${seules} images ; elles sont sur EcoleDirecte.`;
}

/** Ce qu'il advient des images d'une ligne : on les montre, ou on dit pourquoi. */
export type SortDesImages =
  | { forme: "montrees"; urls: string[]; seules: number }
  | { forme: "absentes"; comptees: number }
  | { forme: "aucune" };

/**
 * Trois états, et pas deux — parce que « pas d'image » et « une image que nous
 * n'avons pas su récupérer » ne se disent pas pareil.
 *
 * Une ligne peut porter des jetons sans porter d'octets : les séances importées
 * avant que le connecteur ne garde les images n'en ont aucune en réserve, et
 * une clé qui ne se signe pas donne le même résultat. Afficher un cadre vide
 * ferait exactement ce que le jeton nu faisait — envoyer chercher la panne
 * ailleurs.
 */
export function sortDesImages(contenu: string, jointes: string[] | undefined): SortDesImages {
  const urls = jointes ?? [];
  if (urls.length > 0) return { forme: "montrees", urls, seules: imagesSeules(contenu) };
  const seules = imagesSeules(contenu);
  return seules > 0 ? { forme: "absentes", comptees: seules } : { forme: "aucune" };
}
